import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const GitamEvent = () => {
  const [expandedDomain, setExpandedDomain] = useState(null);

  const useCases = [
    {
      domain: 'Dentistry',
      color: 'from-blue-600 to-blue-800',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      cases: [
        {
          title: 'Automated Dental Caries (Cavity) Detector',
          problem: 'Develop an object detection or classification system to identify dental caries (cavities) in bitewing or panoramic X-rays.',
          background: 'Cavities are the most common dental ailment globally. Early detection prevents painful infections and costly procedures like root canals.',
          requirements: 'An image analysis tool where a dentist uploads an X-ray, and the model draws bounding boxes around suspected cavities with a confidence score.',
          deliverables: 'Trained detection model, a web UI for image uploading, and inference results showing localized findings.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Dental Cavity Detection Dataset (Kaggle)'
        },
        {
          title: 'Individual Tooth Segmentation & Identification',
          problem: 'Build a semantic segmentation model to isolate each tooth and identify it according to universal numbering systems (1-32).',
          background: 'Automated tooth segmentation is the foundational step for digital treatment planning, forensics, and automated charting.',
          requirements: 'A tool that processes a panoramic X-ray (OPG) and returns a color-coded mask where each tooth is a separate segment.',
          deliverables: 'Segmentation mask overlays and a summary report of detected teeth vs. missing teeth.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Teeth Segmentation on Dental X-ray Images'
        },
        {
          title: 'Dental Implant Condition Classifier & Anomaly Detector',
          problem: 'Build a diagnostic system to classify dental implants into clinical categories (Normal, Cavity-involved, Impacted, or Filling) and detect structural anomalies.',
          background: 'Dental implants require long-term monitoring. Detecting anomalies like secondary caries around an implant or peri-implantitis (bone loss) is critical for preventing implant failure.',
          requirements: 'A diagnostic dashboard where a clinician uploads a segmented tooth/implant image and receives a classification label and an anomaly detection score.',
          deliverables: 'A multi-class classifier model, a feature saliency map (to show where the anomaly was detected), and a classification report (F1-score/Accuracy).',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Segmented Dental Radiography (Kaggle)'
        }
      ]
    },
    {
      domain: 'Diabetes',
      color: 'from-green-600 to-green-800',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      cases: [
        {
          title: 'Diabetes Risk & Comorbidity Classifier',
          problem: 'Predict the onset of Diabetes and identify the risk of associated comorbidities (like Hypertension) based on lifestyle and clinical data.',
          background: 'Many individuals are pre-diabetic without knowing. Using routine health data can trigger early lifestyle changes.',
          requirements: 'A "Health Score" calculator where users input their BMI, age, physical activity, and blood pressure to receive a personalized risk report.',
          deliverables: 'A deployed risk-calculator app and a feature-importance report for patient counseling.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Diabetes Health Indicators Dataset'
        },
        {
          title: 'Diagnostic Prediction of Diabetes (Pima Indians)',
          problem: 'Predict whether or not a patient has diabetes based on specific diagnostic measurements (pregnancies, BMI, insulin level, etc.).',
          background: 'This clinical data, sourced from the National Institute of Diabetes and Digestive and Kidney Diseases, focuses on female patients of Pima Indian heritage. Early diagnostic prediction allows for faster clinical intervention.',
          requirements: 'A clinical decision support dashboard where healthcare providers can input patient data (e.g., Glucose, Blood Pressure, Skin Thickness) to receive an immediate diagnostic outcome.',
          deliverables: 'A trained classification model, a feature importance chart (identifying key predictors like BMI or Glucose), and a deployed web application for real-time inference.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Pima Indians Diabetes Database (Kaggle)'
        },
        {
          title: 'Diabetic Retinopathy Grading System',
          problem: 'Build a deep learning model to detect and grade the severity of Diabetic Retinopathy (DR) from retinal fundus images.',
          background: 'DR is a leading cause of blindness. Early screening is essential, but manual grading of eye scans is labor-intensive and requires specialized ophthalmologists.',
          requirements: 'An image upload portal that identifies the presence of DR and classifies it into stages (No DR, Mild, Moderate, Severe, or Proliferative).',
          deliverables: 'A multi-class classification model, a Grad-CAM visualization to show affected retinal areas, and a web-based diagnostic tool.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Diabetic Retinopathy Detection (Kaggle)'
        }
      ]
    },
    {
      domain: 'Radiology',
      color: 'from-purple-600 to-purple-800',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      cases: [
        {
          title: 'Chest X-Ray Pneumonia Classifier',
          problem: 'Automate the detection of Pneumonia from frontal chest X-ray images.',
          background: 'Rapid screening of X-rays in rural areas with limited radiologists can save lives.',
          requirements: 'An upload portal where a user uploads a .jpeg X-ray and gets a "Normal" vs "Pneumonia" result.',
          deliverables: 'Inference script and a Grad-CAM visualization (highlighting the infected area).',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Chest X-Ray Images (Pneumonia)'
        },
        {
          title: 'Brain Tumor Detection (MRI)',
          problem: 'Identify if a brain MRI scan shows the presence of a tumor.',
          background: 'Manual inspection of hundreds of MRI slices is time-consuming; AI assists in preliminary sorting.',
          requirements: 'A drag-and-drop web interface for MRI image analysis.',
          deliverables: 'Trained CNN model and a performance report (Precision/Recall).',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Brain Tumor Detection Dataset'
        },
        {
          title: 'Covid-19 CT-Scan Triage',
          problem: 'Distinguish between Covid-19, Non-Covid Pneumonia, and Healthy lungs using CT scans.',
          background: 'CT scans provide a faster alternative to RT-PCR in some emergency settings for quick isolation.',
          requirements: 'A multi-class classification system for emergency room triage.',
          deliverables: 'Deployed model and an error analysis report (where the model confuses Covid for Pneumonia).',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'SARS-CoV-2 CT-Scan Dataset'
        }
      ]
    },
    {
      domain: 'Financial Sector',
      color: 'from-amber-600 to-amber-800',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      cases: [
        {
          title: 'Credit Card Fraud Detection',
          problem: 'Detect fraudulent transactions within a stream of credit card data.',
          background: 'Fraudulent activities happen in milliseconds; models must be fast and highly accurate.',
          requirements: 'An API that accepts transaction details and returns a "Flag" for suspicious activity.',
          deliverables: 'A Precision-Recall curve and a REST API endpoint.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Credit Card Fraud Dataset'
        },
        {
          title: 'Personal Loan Default Predictor',
          problem: 'Predict whether a customer will default on a loan based on their financial history.',
          background: 'Banks lose billions in NPAs (Non-Performing Assets). Accurate prediction helps in safe lending.',
          requirements: 'A loan approval dashboard for bank managers.',
          deliverables: 'Model evaluation (AUC-ROC) and a simple front-end form.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Lending Club Loan Data'
        },
        {
          title: 'Stock Market Sentiment Analysis',
          problem: 'Classify the sentiment of financial news headlines and predict its impact on stock movement.',
          background: 'Market volatility is often driven by news; NLP can quantify this sentiment.',
          requirements: 'A dashboard that scrapes or accepts news headlines and outputs a "Bullish" or "Bearish" sentiment.',
          deliverables: 'A sentiment analyzer tool and a word cloud of most frequent positive/negative words.',
          dataset: 'https://www.kaggle.com/datasets',
          datasetName: 'Financial Sentiment Analysis'
        }
      ]
    }
  ];

  const day1Schedule = [
    { time: '10:00 AM – 10:15 AM', session: 'Welcome & Introduction', details: 'Introduction to Kodryx AI (kodryx.ai), overview of the event and objectives' },
    { time: '10:15 AM – 11:00 AM', session: 'Use Case Briefing', details: 'Explanation of 3 real-world problem statements provided by Kodryx AI (Participants will build solutions without using external AI APIs such as OpenAI and GPTs)' },
    { time: '11:00 AM – 4:00 PM', session: 'Hackathon Begins', details: 'Students work in teams on the proposed use cases with guidance from the Kodryx team' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>AI Day @ GITAM University — March 5–6, 2026 | Hyderabad</title>
        <meta name="description" content="AI Day at GITAM University, Hyderabad — A 2-day AI hackathon with 12 real-world use cases across Dentistry, Diabetes, Radiology & Finance. Organized by Kodryx AI & GITAM School of CS&E. March 5–6, 2026." />
        <meta name="keywords" content="AI Day, GITAM University, Hyderabad, AI hackathon, Kodryx AI, Dentistry AI, Diabetes AI, Radiology AI, Financial AI, machine learning, March 2026" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Day @ GITAM University — March 5–6, 2026" />
        <meta property="og:description" content="A 2-day AI hackathon at GITAM University, Hyderabad with 12 real-world use cases across Dentistry, Diabetes, Radiology & Finance. 100–120 students. No external AI APIs allowed!" />
        <meta property="og:url" content="https://aidayevents.com/events/gitam-university" />
        <meta property="og:image" content="https://aidayevents.com/images/backgrounds/GITAM_Hyderabad_Campus.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="AI Day Events" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Day @ GITAM University — March 5–6, 2026" />
        <meta name="twitter:description" content="A 2-day AI hackathon at GITAM University, Hyderabad with 12 real-world use cases across Dentistry, Diabetes, Radiology & Finance." />
        <meta name="twitter:image" content="https://aidayevents.com/images/backgrounds/GITAM_Hyderabad_Campus.jpg" />
        <link rel="canonical" href="https://aidayevents.com/events/gitam-university" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-black pt-32 pb-20">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/images/backgrounds/GITAM_Hyderabad_Campus.jpg" 
            alt="GITAM University Hyderabad" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-crimson font-outfit font-800 text-lg mb-4 uppercase tracking-wide">
              GITAM University Edition
            </p>
            <h1 className="text-4xl md:text-6xl font-outfit font-800 text-white mb-6">
              AI Day @ GITAM University
            </h1>
            <p className="text-gray-400 font-outfit font-600 text-lg mb-2">
              Organized in collaboration with GITAM School of Computer Science & Engineering
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-gray-300 font-outfit font-600 text-lg mb-8 mt-6">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                5 – 6 March 2026
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Hyderabad, Telangana
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                100 – 120 Students
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                10:00 AM IST
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#use-cases" className="btn-primary">
                View Use Cases
              </a>
              <a href="#agenda" className="btn-secondary-light">
                View Event Agenda
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About this Edition */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-8 text-center">
              About this Edition
            </h2>
            <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
              <p className="text-lg font-outfit font-600 text-gray-700 leading-relaxed mb-6">
                GITAM University in collaboration with <a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="text-crimson font-800 hover:underline">Kodryx AI</a> presents 
                a 2-day AI hackathon experience — where students tackle real-world problem statements across healthcare and finance domains, 
                building end-to-end AI solutions without using external AI APIs.
              </p>
              <div className="border-l-4 border-crimson pl-6">
                <h3 className="text-xl font-outfit font-800 text-black mb-3">About GITAM University</h3>
                <p className="text-gray-700 font-outfit font-600 mb-4">
                  GITAM (Gandhi Institute of Technology and Management) is a premier deemed-to-be university in Hyderabad, 
                  known for its excellence in engineering, science, and technology education.
                </p>
                <ul className="space-y-2 text-gray-700 font-outfit font-600">
                  <li className="flex items-start gap-2">
                    <img src="/images/icons/check-icon.svg" alt="Check" className="w-5 h-5 mt-1" />
                    <span>School of Computer Science & Engineering with cutting-edge AI curriculum</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <img src="/images/icons/check-icon.svg" alt="Check" className="w-5 h-5 mt-1" />
                    <span>State-of-the-art labs and research facilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <img src="/images/icons/check-icon.svg" alt="Check" className="w-5 h-5 mt-1" />
                    <span>Strong industry partnerships and placement record</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Format */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-4 text-center">
            Event Format
          </h2>
          <p className="text-center text-gray-600 font-outfit font-600 mb-12">
            A 2-day AI hackathon with real-world problem statements
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Day 1 */}
            <div className="bg-gray-50 border-2 border-gray-200 p-8 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-outfit font-800 text-black mb-2">Day 1 — March 5, 2026</h3>
              <p className="text-sm font-outfit font-600 text-crimson mb-3">Hackathon Venue: Lab 1 & Lab 2</p>
              <p className="text-gray-600 font-outfit font-600">
                Use case briefing, team formation, and hackathon kickoff. Students work on real-world AI problem statements with mentorship from the Kodryx team.
              </p>
            </div>

            {/* Day 2 */}
            <div className="bg-gray-50 border-2 border-gray-200 p-8 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-outfit font-800 text-black mb-2">Day 2 — March 6, 2026</h3>
              <p className="text-sm font-outfit font-600 text-crimson mb-3">Venue: Shivaji Auditorium</p>
              <p className="text-gray-600 font-outfit font-600">
                Project presentations, demos, judging, and awards ceremony. All 2nd and 3rd year students gather at the auditorium for the grand showcase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-4 text-center">
            Hackathon Use Cases
          </h2>
          <p className="text-center text-gray-600 font-outfit font-600 mb-4">
            4 Domains &middot; 12 Real-World Problem Statements
          </p>
          <p className="text-center text-crimson font-outfit font-800 mb-12 text-sm uppercase tracking-wide">
            No external AI APIs (OpenAI, GPTs, etc.) allowed — Build from scratch!
          </p>

          <div className="space-y-6">
            {useCases.map((domain, domainIndex) => (
              <div key={domainIndex} className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm">
                {/* Domain Header */}
                <button
                  onClick={() => setExpandedDomain(expandedDomain === domainIndex ? null : domainIndex)}
                  className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${domain.color} rounded-lg flex items-center justify-center text-white`}>
                      {domain.icon}
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl md:text-2xl font-outfit font-800 text-black">
                        {domain.domain}
                      </h3>
                      <p className="text-gray-500 font-outfit font-600 text-sm">
                        {domain.cases.length} Use Cases
                      </p>
                    </div>
                  </div>
                  <svg 
                    className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${expandedDomain === domainIndex ? 'rotate-180' : ''}`} 
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Use Cases */}
                {expandedDomain === domainIndex && (
                  <div className="border-t border-gray-200 p-6 space-y-6">
                    {domain.cases.map((useCase, caseIndex) => (
                      <div key={caseIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-outfit font-800 text-black mb-4 flex items-start gap-2">
                          <span className="bg-crimson text-white text-sm font-800 px-2 py-1 rounded flex-shrink-0">
                            UC-{caseIndex + 1}
                          </span>
                          {useCase.title}
                        </h4>
                        
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm font-outfit font-800 text-crimson uppercase tracking-wide mb-1">Problem Statement</p>
                            <p className="text-gray-700 font-outfit font-600">{useCase.problem}</p>
                          </div>
                          <div>
                            <p className="text-sm font-outfit font-800 text-crimson uppercase tracking-wide mb-1">Background</p>
                            <p className="text-gray-700 font-outfit font-600">{useCase.background}</p>
                          </div>
                          <div>
                            <p className="text-sm font-outfit font-800 text-crimson uppercase tracking-wide mb-1">Functional Requirements</p>
                            <p className="text-gray-700 font-outfit font-600">{useCase.requirements}</p>
                          </div>
                          <div>
                            <p className="text-sm font-outfit font-800 text-crimson uppercase tracking-wide mb-1">Deliverables</p>
                            <p className="text-gray-700 font-outfit font-600">{useCase.deliverables}</p>
                          </div>
                          <div className="pt-2">
                            <a 
                              href={useCase.dataset}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-crimson font-outfit font-800 hover:underline text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Dataset: {useCase.datasetName}
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Day 1 Agenda */}
      <section id="agenda" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-4 text-center">
            Agenda & Schedule
          </h2>
          <p className="text-center text-gray-600 font-outfit font-600 mb-12">
            5 – 6 March 2026 @ GITAM University, Hyderabad
          </p>

          {/* Day 1 */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-crimson rounded-lg flex items-center justify-center">
                <span className="text-white font-outfit font-800 text-sm">D1</span>
              </div>
              <div>
                <h3 className="text-2xl font-outfit font-800 text-black">Day 1 — March 5, 2026</h3>
                <p className="text-gray-500 font-outfit font-600 text-sm">Hackathon Venue: Lab 1 & Lab 2</p>
              </div>
            </div>

            <div className="space-y-4">
              {day1Schedule.map((item, index) => (
                <div key={index} className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 hover:border-crimson transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-shrink-0">
                      <span className="bg-black text-white font-outfit font-800 text-sm px-3 py-2 rounded-lg inline-block whitespace-nowrap">
                        {item.time}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-lg font-outfit font-800 text-black mb-1">{item.session}</h4>
                      <p className="text-gray-600 font-outfit font-600">{item.details}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Day 2 */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-crimson rounded-lg flex items-center justify-center">
                <span className="text-white font-outfit font-800 text-sm">D2</span>
              </div>
              <div>
                <h3 className="text-2xl font-outfit font-800 text-black">Day 2 — March 6, 2026</h3>
                <p className="text-gray-500 font-outfit font-600 text-sm">Venue: Shivaji Auditorium (all 2nd & 3rd year students)</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-crimson to-black p-8 rounded-lg shadow-xl text-center">
              <h3 className="text-xl md:text-2xl font-outfit font-800 text-white mb-4">
                Presentations, Demos & Awards
              </h3>
              <p className="text-white/80 font-outfit font-600 mb-2">
                Teams present their hackathon solutions to judges and the student body.
              </p>
              <p className="text-white/80 font-outfit font-600">
                Detailed Day 2 schedule will be announced soon.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-black border-2 border-crimson p-8 rounded-lg text-center">
            <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-outfit font-800 text-white mb-3">Important Rule</h3>
            <p className="text-gray-300 font-outfit font-600 text-lg">
              Participants will build solutions <span className="text-crimson font-800">without using external AI APIs</span> such as OpenAI, GPTs, or similar services. 
              All models must be trained and deployed from scratch.
            </p>
          </div>
        </div>
      </section>

      {/* Why Attend */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-white mb-12 text-center">
              Why Attend AI Day?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-white mb-3">Build Real AI</h3>
                <p className="text-gray-300 font-outfit font-600">
                  Work on industry-grade problem statements across healthcare and finance. Build end-to-end ML pipelines — not just toy projects.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-white mb-3">Get Mentorship</h3>
                <p className="text-gray-300 font-outfit font-600">
                  Receive hands-on guidance from the <a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Kodryx AI</a> team throughout the hackathon. Learn best practices in ML engineering.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-white mb-3">Win & Showcase</h3>
                <p className="text-gray-300 font-outfit font-600">
                  Present your solutions to judges, win exciting prizes, and get recognized for your AI skills in front of the entire student body.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Incentives */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-crimson">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-white mb-4">
              Benefits & Incentives
            </h2>
            <p className="text-lg font-outfit font-600 text-gray-300">
              Exciting rewards for top-performing hackathon teams
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">YouTube Feature</h3>
              <p className="text-gray-300 font-outfit font-600">
                Top teams' projects featured on <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> YouTube channel and community platform
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Internship Opportunities</h3>
              <p className="text-gray-300 font-outfit font-600">
                Internship opportunities with <a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Kodryx AI</a> and participating companies
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Interview Opportunities</h3>
              <p className="text-gray-300 font-outfit font-600">
                Direct interview opportunities with participating companies
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Student Ambassador</h3>
              <p className="text-gray-300 font-outfit font-600">
                Become an <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> student ambassador and lead community initiatives
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Certificates</h3>
              <p className="text-gray-300 font-outfit font-600">
                Participation and winner certificates for all hackathon participants
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Goodies & Swag</h3>
              <p className="text-gray-300 font-outfit font-600">
                Exclusive <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> goodies and swag for participants
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Venue & Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-12 text-center">
            Venue & Location
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="bg-gray-50 p-8 rounded-lg border-2 border-gray-200">
                <h3 className="text-2xl font-outfit font-800 text-black mb-6">GITAM University</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-crimson flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="font-outfit font-800 text-black">Address</p>
                      <p className="text-gray-600 font-outfit font-600">
                        Gitam University Rd, Rudraram<br />
                        Telangana 502329
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-crimson flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-outfit font-800 text-black">Date & Time</p>
                      <p className="text-gray-600 font-outfit font-600">
                        Thursday – Friday, 5 – 6 March 2026<br />
                        10:00 AM IST onwards
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-crimson flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <div>
                      <p className="font-outfit font-800 text-black">Venues</p>
                      <p className="text-gray-600 font-outfit font-600">
                        Day 1: Lab 1 & Lab 2 (Hackathon)<br />
                        Day 2: Shivaji Auditorium (Presentations)
                      </p>
                    </div>
                  </div>
                </div>
                <a 
                  href="https://maps.google.com/?q=GITAM+University+Hyderabad+Rudraram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary mt-6 inline-block"
                >
                  View on Google Maps
                </a>
              </div>
            </div>
            <div className="h-96 rounded-lg border-2 border-gray-300 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.1!2d78.3745!3d17.4574!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb93dfa4d4b037%3A0x17e1c7ec76d3b0e0!2sGITAM%20University%20-%20Hyderabad!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="GITAM University Hyderabad Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GitamEvent;
